import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Avatar
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import { updateDataset, uploadDatasetIconFromUrl } from '../services/api/datasetService';

/**
 * EditDatasetDialog - Dialog for editing dataset metadata
 *
 * Props:
 * - open: Boolean, controls dialog visibility
 * - dataset: Dataset object to edit
 * - onClose: Function called when dialog closes
 * - onUpdated: Function called when dataset successfully updated
 */
export default function EditDatasetDialog({ open, dataset, onClose, onUpdated }) {
  const [datasetName, setDatasetName] = useState('');
  const [description, setDescription] = useState('');
  const [iconUrl, setIconUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form when dataset changes
  useEffect(() => {
    if (dataset) {
      setDatasetName(dataset.dataset_name || '');
      setDescription(dataset.description || '');
      setIconUrl(dataset.icon_url || '');
    }
  }, [dataset]);

  function handleClose() {
    if (!submitting) {
      setError(null);
      onClose();
    }
  }

  async function handleSubmit() {
    if (!datasetName.trim()) {
      setError('Dataset name is required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const updates = {
        dataset_name: datasetName.trim(),
        description: description.trim() || undefined
      };

      // Check if icon URL changed
      const iconChanged = iconUrl.trim() !== (dataset.icon_url || '');

      // Update dataset metadata
      const updatedDataset = await updateDataset(dataset.dataset_id, updates);

      // If icon URL changed and is not empty, upload new icon
      if (iconChanged && iconUrl.trim()) {
        try {
          await uploadDatasetIconFromUrl(dataset.dataset_id, iconUrl.trim());
        } catch (iconError) {
          console.error('Failed to upload icon:', iconError);
          setError(`Dataset updated, but icon upload failed: ${iconError.message}`);
          return; // Don't close dialog if icon failed
        }
      }

      // Notify parent
      onUpdated(updatedDataset);
      handleClose();
    } catch (err) {
      console.error('Failed to update dataset:', err);
      setError(err.message || 'Failed to update dataset');
    } finally {
      setSubmitting(false);
    }
  }

  if (!dataset) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Edit Dataset</DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Dataset ID (read-only) */}
        <TextField
          margin="dense"
          label="Dataset ID"
          fullWidth
          value={dataset.dataset_id}
          disabled
          helperText="Dataset ID cannot be changed"
          sx={{ mb: 1 }}
        />

        <TextField
          autoFocus
          margin="dense"
          label="Dataset Name"
          fullWidth
          required
          value={datasetName}
          onChange={(e) => setDatasetName(e.target.value)}
          disabled={submitting}
          placeholder="e.g., MI5 Personnel, London Office Staff"
        />

        <TextField
          margin="dense"
          label="Description"
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={submitting}
          placeholder="Optional description of this dataset"
        />

        <TextField
          margin="dense"
          label="Icon URL"
          fullWidth
          value={iconUrl}
          onChange={(e) => setIconUrl(e.target.value)}
          disabled={submitting}
          placeholder="https://example.com/icon.png (optional)"
          helperText="URL to an icon image for this dataset"
        />

        {/* Icon Preview */}
        {iconUrl && (
          <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Preview:
            </Typography>
            <Avatar
              src={iconUrl}
              variant="rounded"
              sx={{
                width: 48,
                height: 48,
                bgcolor: 'grey.100'
              }}
            >
              <ImageIcon sx={{ color: 'text.disabled' }} />
            </Avatar>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={submitting || !datasetName.trim()}
        >
          {submitting ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
